import { useEffect, useRef, useState } from "react";
import { api, formatUSD } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Loader2, Pencil, Package, EyeOff, ImagePlus, Trash2, Save } from "lucide-react";

const inputCls = "bg-zinc-950 border-zinc-800 focus:border-[#FF5A00] focus-visible:ring-[#FF5A00]/30 rounded-sm";

const Fld = ({ label, children }) => (
  <div>
    <Label className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1.5 block">{label}</Label>
    {children}
  </div>
);

export const RentalCatalogManager = () => {
  const [products, setProducts] = useState(null);
  const [editing, setEditing] = useState(null);

  const load = () => api.get("/admin/rental-products").then(({ data }) => setProducts(data)).catch(() => toast.error("No se pudo cargar el catálogo"));
  useEffect(() => { load(); }, []);

  const quickUpdate = async (p, patch, msg) => {
    try {
      await api.put(`/admin/rental-products/${p.id}`, patch);
      toast.success(msg);
      load();
    } catch { toast.error("No se pudo actualizar"); }
  };

  if (!products) return <div className="py-16 text-center text-zinc-500 text-sm"><Loader2 className="w-5 h-5 animate-spin inline mr-2" /> Cargando catálogo...</div>;

  return (
    <div>
      <div className="text-xs text-zinc-500 mb-4">{products.length} productos · Los cambios impactan al instante en el catálogo que ven los expositores. Los productos ocultos o agotados no pueden agregarse a nuevos pedidos.</div>
      <Card className="bg-zinc-900 border-zinc-800 rounded-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-zinc-950 text-[10px] uppercase tracking-wider text-zinc-500">
              <tr>
                <th className="text-left p-3">Producto</th>
                <th className="text-right p-3">Precio USD</th>
                <th className="text-right p-3">Stock</th>
                <th className="text-center p-3">Agotado</th>
                <th className="text-center p-3">Oculto</th>
                <th className="p-3"></th>
              </tr>
            </thead>
            <tbody>
              {products.map((p) => (
                <tr key={p.id} data-testid={`catalogo-row-${p.id}`} className={`border-t border-zinc-800 hover:bg-zinc-900/50 transition ${p.hidden ? "opacity-50" : ""}`}>
                  <td className="p-3">
                    <div className="flex items-center gap-3">
                      <div className="w-11 h-11 rounded-sm bg-zinc-950 border border-zinc-800 grid place-items-center overflow-hidden shrink-0">
                        {p.image ? <img src={p.image} alt={p.name} className="w-full h-full object-cover" /> : <Package className="w-4 h-4 text-zinc-700" />}
                      </div>
                      <div className="min-w-0">
                        <div className="font-medium truncate flex items-center gap-2">
                          {p.name}
                          {p.hidden && <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm text-[9px] bg-zinc-800 text-zinc-400 border border-zinc-700"><EyeOff className="w-2.5 h-2.5" /> OCULTO</span>}
                        </div>
                        <div className="text-[11px] text-zinc-500 font-mono">{p.medidas || p.category}</div>
                      </div>
                    </div>
                  </td>
                  <td className="p-3 text-right font-mono">
                    {p.a_cotizar || p.price == null ? <span className="text-amber-300 text-[10px] uppercase">A cotizar</span> : <span className="text-[#FF5A00]">{formatUSD(p.price)}</span>}
                  </td>
                  <td className="p-3 text-right font-mono text-zinc-400">{p.stock_qty ?? "—"}</td>
                  <td className="p-3 text-center">
                    <Switch
                      data-testid={`toggle-agotado-${p.id}`}
                      checked={p.stock_status === "agotado"}
                      onCheckedChange={(v) => quickUpdate(p, { stock_status: v ? "agotado" : "disponible" }, v ? "Producto marcado como AGOTADO" : "Producto disponible nuevamente")}
                      className="data-[state=checked]:bg-red-500"
                    />
                  </td>
                  <td className="p-3 text-center">
                    <Switch
                      data-testid={`toggle-oculto-${p.id}`}
                      checked={!!p.hidden}
                      onCheckedChange={(v) => quickUpdate(p, { hidden: v }, v ? "Producto oculto del catálogo" : "Producto visible en el catálogo")}
                      className="data-[state=checked]:bg-zinc-500"
                    />
                  </td>
                  <td className="p-3 text-right">
                    <Button data-testid={`editar-producto-${p.id}`} size="sm" variant="outline" className="border-zinc-700 hover:bg-zinc-800 rounded-sm h-8 text-xs" onClick={() => setEditing(p)}>
                      <Pencil className="w-3 h-3 mr-1.5" /> Editar
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
      <ProductEditDialog product={editing} onClose={() => setEditing(null)} reload={load} />
    </div>
  );
};

function ProductEditDialog({ product, onClose, reload }) {
  const [f, setF] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    if (product) setF({
      name: product.name, medidas: product.medidas || "", description: product.description || "",
      price: product.price ?? "", stock_qty: product.stock_qty ?? "", a_cotizar: !!product.a_cotizar,
      stock_status: product.stock_status || "disponible", hidden: !!product.hidden, image: product.image || "",
    });
  }, [product]);

  if (!product || !f) return null;

  const save = async () => {
    if (!f.name.trim()) return toast.error("El nombre es obligatorio");
    if (!f.a_cotizar && (f.price === "" || Number(f.price) < 0)) return toast.error("Ingresá un precio válido o marcá 'A cotizar'");
    setSaving(true);
    try {
      const payload = {
        name: f.name.trim(), medidas: f.medidas, description: f.description,
        a_cotizar: f.a_cotizar, stock_status: f.stock_status, hidden: f.hidden,
      };
      if (f.price !== "") payload.price = Number(f.price);
      if (f.stock_qty !== "") payload.stock_qty = parseInt(f.stock_qty, 10);
      await api.put(`/admin/rental-products/${product.id}`, payload);
      toast.success("Producto actualizado");
      reload();
      onClose();
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "No se pudo guardar");
    } finally { setSaving(false); }
  };

  const uploadImage = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/admin/rental-products/${product.id}/image`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setF((prev) => ({ ...prev, image: data.image }));
      toast.success("Foto actualizada");
      reload();
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "No se pudo subir la foto");
    } finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const removeImage = async () => {
    try {
      await api.delete(`/admin/rental-products/${product.id}/image`);
      setF((prev) => ({ ...prev, image: "" }));
      toast.success("Foto eliminada");
      reload();
    } catch { toast.error("No se pudo eliminar la foto"); }
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent data-testid="producto-edit-dialog" className="bg-zinc-950 border-zinc-800 text-white max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="text-base">Editar producto</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="flex items-start gap-4">
            <div className="w-24 h-24 rounded-sm bg-zinc-900 border border-zinc-800 grid place-items-center overflow-hidden shrink-0">
              {uploading ? <Loader2 className="w-5 h-5 animate-spin text-zinc-500" /> : f.image ? <img src={f.image} alt="" className="w-full h-full object-cover" /> : <Package className="w-7 h-7 text-zinc-700" />}
            </div>
            <div className="space-y-2">
              <input ref={fileRef} type="file" accept="image/*" className="hidden" data-testid="producto-image-input" onChange={(e) => uploadImage(e.target.files?.[0])} />
              <Button data-testid="producto-image-upload-btn" size="sm" variant="outline" disabled={uploading} className="border-zinc-700 hover:bg-zinc-800 rounded-sm h-8 text-xs" onClick={() => fileRef.current?.click()}>
                <ImagePlus className="w-3 h-3 mr-1.5" /> {f.image ? "Cambiar foto" : "Subir foto"}
              </Button>
              {f.image && (
                <Button data-testid="producto-image-delete-btn" size="sm" variant="outline" className="border-zinc-800 hover:bg-red-500/10 hover:text-red-300 rounded-sm h-8 text-xs block" onClick={removeImage}>
                  <Trash2 className="w-3 h-3 mr-1.5" /> Quitar foto
                </Button>
              )}
              <div className="text-[10px] text-zinc-600">JPG, PNG o WEBP · máx 8 MB · se optimiza automáticamente</div>
            </div>
          </div>

          <Fld label="Nombre"><Input data-testid="producto-edit-name" className={inputCls} value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} /></Fld>
          <div className="grid grid-cols-2 gap-3">
            <Fld label="Medidas"><Input data-testid="producto-edit-medidas" className={inputCls} value={f.medidas} onChange={(e) => setF({ ...f, medidas: e.target.value })} /></Fld>
            <Fld label="Stock (unidades)"><Input data-testid="producto-edit-stock" type="number" min="0" className={inputCls} value={f.stock_qty} placeholder="Sin definir" onChange={(e) => setF({ ...f, stock_qty: e.target.value })} /></Fld>
          </div>
          <Fld label="Descripción"><Textarea data-testid="producto-edit-desc" rows={2} className={inputCls} value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} /></Fld>
          <div className="grid grid-cols-2 gap-3 items-end">
            <Fld label="Precio (USD + IVA)">
              <Input data-testid="producto-edit-price" type="number" min="0" step="1" disabled={f.a_cotizar} className={inputCls} value={f.price} onChange={(e) => setF({ ...f, price: e.target.value })} />
            </Fld>
            <label className="flex items-center gap-2 pb-2 cursor-pointer">
              <Switch data-testid="producto-edit-acotizar" checked={f.a_cotizar} onCheckedChange={(v) => setF({ ...f, a_cotizar: v })} className="data-[state=checked]:bg-amber-500" />
              <span className="text-xs text-zinc-300">Precio "A COTIZAR"</span>
            </label>
          </div>
          <div className="flex items-center gap-6 border border-zinc-800 rounded-sm p-3 bg-zinc-900">
            <label className="flex items-center gap-2 cursor-pointer">
              <Switch data-testid="producto-edit-agotado" checked={f.stock_status === "agotado"} onCheckedChange={(v) => setF({ ...f, stock_status: v ? "agotado" : "disponible" })} className="data-[state=checked]:bg-red-500" />
              <span className="text-xs text-zinc-300">Agotado</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <Switch data-testid="producto-edit-oculto" checked={f.hidden} onCheckedChange={(v) => setF({ ...f, hidden: v })} className="data-[state=checked]:bg-zinc-500" />
              <span className="text-xs text-zinc-300">Ocultar del catálogo</span>
            </label>
          </div>
          <Button data-testid="producto-edit-save-btn" onClick={save} disabled={saving} className="w-full bg-[#FF5A00] hover:bg-[#E04F00] rounded-sm h-10">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4 mr-2" /> Guardar cambios</>}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
